/**
 * LOCATION: dashboard-business/src/pages/Onboarding.js
 * Upgraded onboarding — FA icons, no emojis, sleek dark-card design
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/auth';

// Load Font Awesome
const loadFA = () => {
  if (!document.getElementById('fa-cdn-onb')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-onb'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

const STEPS = [
  { id: 1, label: 'Welcome',    icon: 'fa-hand-wave' },
  { id: 2, label: 'API Key',    icon: 'fa-key' },
  { id: 3, label: 'Install',    icon: 'fa-code' },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep]     = useState(1);
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [userData, setUserData] = useState(() => AuthService.getCurrentUser() || {});

  useEffect(() => { loadFA(); }, []);

  const handleComplete = () => {
    localStorage.setItem('janimony_onboarding_complete', 'true');
    window.location.href = '/dashboard';
  };

  const copyKey = () => {
    navigator.clipboard.writeText(userData.apiKey || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const installCode = `<script src="https://cdn.janimony.chat/widget.js"><\/script>
<script>
  JanimonyChat.init({
    businessId: '${userData.apiKey || 'YOUR_API_KEY'}'
  });
<\/script>`;

  const copyCode = () => {
    navigator.clipboard.writeText(installCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2500);
  };

  // ── Step 1: Welcome ─────────────────────────────────────────
  const WelcomeStep = () => (
    <div style={s.step}>
      <div style={s.stepIconWrap}>
        <i className="fa-solid fa-party-horn" style={{ fontSize: 36, color: '#6c63ff' }}></i>
      </div>
      <h1 style={s.stepTitle}>Welcome to Janimony.chat!</h1>
      <p style={s.stepSub}>Your account is ready. Let's get your chat widget live in 3 steps.</p>

      <div style={s.featureRow}>
        {[
          { icon: 'fa-robot',     color: '#6c63ff', label: 'AI-Powered',   desc: 'Gemini AI answers instantly' },
          { icon: 'fa-users',     color: '#c084fc', label: 'Human Backup', desc: 'Seamless agent handoff' },
          { icon: 'fa-bolt',      color: '#f59e0b', label: 'Easy Setup',   desc: 'Live in under 60 seconds' },
        ].map(f => (
          <div key={f.label} style={s.featureCard}>
            <div style={{ ...s.featureIcon, background: f.color + '18' }}>
              <i className={`fa-solid ${f.icon}`} style={{ color: f.color, fontSize: 22 }}></i>
            </div>
            <div style={s.featureLabel}>{f.label}</div>
            <div style={s.featureDesc}>{f.desc}</div>
          </div>
        ))}
      </div>

      <button onClick={() => setStep(2)} style={s.primaryBtn}>
        <i className="fa-solid fa-arrow-right"></i> Get Started
      </button>
    </div>
  );

  // ── Step 2: API Key ──────────────────────────────────────────
  const ApiKeyStep = () => (
    <div style={s.step}>
      <div style={s.stepIconWrap}>
        <i className="fa-solid fa-key" style={{ fontSize: 36, color: '#6c63ff' }}></i>
      </div>
      <h1 style={s.stepTitle}>Your API Key</h1>
      <p style={s.stepSub}>This is your unique widget identifier. You'll need it to install the chat widget on your website.</p>

      <div style={s.apiBox}>
        <div style={s.apiBoxLabel}>
          <i className="fa-solid fa-key" style={{ marginRight: 7, color: '#6c63ff', fontSize: 12 }}></i>
          Your API Key
        </div>
        <div style={s.apiKeyRow}>
          <code style={s.apiCode}>{userData.apiKey || 'Loading...'}</code>
          <button onClick={copyKey} style={{ ...s.copyBtn, background: copied ? '#dcfce7' : 'rgba(255,255,255,0.08)', color: copied ? '#166534' : 'rgba(255,255,255,0.7)' }}>
            <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} style={{ marginRight: 5 }}></i>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      <div style={s.infoBox}>
        <i className="fa-solid fa-circle-info" style={{ color: '#6c63ff', fontSize: 14, flexShrink: 0 }}></i>
        <span>Keep this key safe. You can find it anytime in your <strong>Dashboard → Overview tab</strong>.</span>
      </div>

      <div style={s.btnRow}>
        <button onClick={() => setStep(1)} style={s.ghostBtn}><i className="fa-solid fa-arrow-left"></i> Back</button>
        <button onClick={() => setStep(3)} style={s.primaryBtn}><i className="fa-solid fa-arrow-right"></i> Continue</button>
      </div>
    </div>
  );

  // ── Step 3: Install ─────────────────────────────────────────
  const InstallStep = () => (
    <div style={s.step}>
      <div style={s.stepIconWrap}>
        <i className="fa-solid fa-rocket" style={{ fontSize: 36, color: '#6c63ff' }}></i>
      </div>
      <h1 style={s.stepTitle}>Install Your Widget</h1>
      <p style={s.stepSub}>
        Copy this code snippet and paste it before the closing <code style={s.inlineCode}>&lt;/body&gt;</code> tag
        on any page where you want the chat widget.
      </p>

      <div style={s.codeBox}>
        <div style={s.codeBoxBar}>
          <div style={{ display: 'flex', gap: 5 }}>
            {['#ef4444','#f59e0b','#22c55e'].map(c => <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
          </div>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: 0.5 }}>HTML</span>
          <button onClick={copyCode} style={{ ...s.copyBtn, marginLeft: 'auto', background: codeCopied ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.08)', color: codeCopied ? '#4ade80' : 'rgba(255,255,255,0.6)', fontSize: 11 }}>
            <i className={`fa-solid ${codeCopied ? 'fa-check' : 'fa-copy'}`} style={{ marginRight: 4 }}></i>
            {codeCopied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre style={s.code}>
          <span style={{ color: '#64748b' }}>{`<!-- Janimony Chat Widget -->`}</span>{'\n'}
          <span style={{ color: '#7dd3fc' }}>&lt;script </span><span style={{ color: '#86efac' }}>src</span><span style={{ color: '#94a3b8' }}>="</span><span style={{ color: '#fcd34d' }}>https://cdn.janimony.chat/widget.js</span><span style={{ color: '#94a3b8' }}>"&gt;&lt;/script&gt;</span>{'\n'}
          <span style={{ color: '#7dd3fc' }}>&lt;script&gt;</span>{'\n'}
          {'  '}<span style={{ color: '#c4b5fd' }}>JanimonyChat</span><span style={{ color: '#94a3b8' }}>.</span><span style={{ color: '#86efac' }}>init</span><span style={{ color: '#94a3b8' }}>(&#123;</span>{'\n'}
          {'    '}<span style={{ color: '#fda4af' }}>businessId</span><span style={{ color: '#94a3b8' }}>: '</span><span style={{ color: '#fcd34d' }}>{userData.apiKey || 'YOUR_API_KEY'}</span><span style={{ color: '#94a3b8' }}>'</span>{'\n'}
          {'  '}<span style={{ color: '#94a3b8' }}>&#125;);</span>{'\n'}
          <span style={{ color: '#7dd3fc' }}>&lt;/script&gt;</span>
        </pre>
      </div>

      <div style={s.platformRow}>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 10, textAlign: 'center' }}>Works on every platform</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          {['WordPress', 'Shopify', 'Wix', 'Webflow', 'Custom HTML', 'React', 'Vue'].map(p => (
            <span key={p} style={s.platformTag}>{p}</span>
          ))}
        </div>
      </div>

      <div style={s.btnRow}>
        <button onClick={() => setStep(2)} style={s.ghostBtn}><i className="fa-solid fa-arrow-left"></i> Back</button>
        <button onClick={handleComplete} style={{ ...s.primaryBtn, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
          <i className="fa-solid fa-gauge"></i> Go to Dashboard
        </button>
      </div>
    </div>
  );

  return (
    <div style={s.container}>

      {/* Logo */}
<div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
  <img 
    src={`${process.env.PUBLIC_URL}/janimony-logo.png`} 
    alt="Janimony" 
    style={{ width: '80px', height: 'auto', objectFit: 'contain' }}
  />
</div>
      {/* Background orbs */}
      <div style={s.orb1}></div>
      <div style={s.orb2}></div>

      {/* Progress */}
      <div style={s.progressWrap}>
        {/* Step pills */}
        <div style={s.stepPills}>
          {STEPS.map((st, idx) => (
            <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ ...s.pill, ...(step >= st.id ? s.pillActive : {}), ...(step === st.id ? s.pillCurrent : {}) }}>
                  {step > st.id
                    ? <i className="fa-solid fa-check" style={{ fontSize: 11 }}></i>
                    : <i className={`fa-solid ${st.icon}`} style={{ fontSize: 11 }}></i>}
                </div>
                <span style={{ fontSize: 12, fontWeight: step === st.id ? 700 : 500, color: step >= st.id ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)' }}>{st.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div style={{ width: 32, height: 1, background: step > st.id ? 'rgba(108,99,255,0.6)' : 'rgba(255,255,255,0.1)' }} />
              )}
            </div>
          ))}
        </div>
        {/* Progress bar */}
        <div style={s.progressBar}>
          <div style={{ ...s.progressFill, width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }} />
        </div>
      </div>

      {/* Card */}
      <div style={s.card}>
        {step === 1 && <WelcomeStep />}
        {step === 2 && <ApiKeyStep />}
        {step === 3 && <InstallStep />}
      </div>

      {/* Skip */}
      <button onClick={handleComplete} style={s.skipBtn}>
        <i className="fa-solid fa-forward-fast" style={{ marginRight: 6, fontSize: 11 }}></i>
        Skip for now
      </button>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────
const s = {
  container: {
    minHeight: '100vh',
    background: '#0a0a0a',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: 24, fontFamily: "'DM Sans', sans-serif",
    position: 'relative', overflow: 'hidden',
  },
  orb1: {
    position: 'absolute', width: 400, height: 400, borderRadius: '50%',
    background: '#6c63ff', filter: 'blur(100px)', opacity: 0.08,
    top: -100, right: -100, pointerEvents: 'none',
  },
  orb2: {
    position: 'absolute', width: 300, height: 300, borderRadius: '50%',
    background: '#c084fc', filter: 'blur(80px)', opacity: 0.06,
    bottom: -80, left: -80, pointerEvents: 'none',
  },

  progressWrap: { width: '100%', maxWidth: 560, marginBottom: 28, position: 'relative', zIndex: 1 },
  stepPills: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 14 },
  pill: {
    width: 28, height: 28, borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'rgba(255,255,255,0.3)', transition: 'all 0.3s',
  },
  pillActive: { background: 'rgba(108,99,255,0.3)', border: '1px solid rgba(108,99,255,0.6)', color: '#c4b5fd' },
  pillCurrent: { background: '#6c63ff', border: '1px solid #8b82ff', color: '#fff', boxShadow: '0 0 12px rgba(108,99,255,0.5)' },
  progressBar: { height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg,#6c63ff,#c084fc)', borderRadius: 10, transition: 'width 0.5s ease' },

  card: {
    background: '#111114', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20, padding: '48px 44px',
    width: '100%', maxWidth: 560,
    boxShadow: '0 40px 80px rgba(0,0,0,0.6)',
    position: 'relative', zIndex: 1,
  },

  step: { textAlign: 'center' },
  stepIconWrap: {
    width: 72, height: 72, borderRadius: 20,
    background: 'rgba(108,99,255,0.12)', border: '1px solid rgba(108,99,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 22px',
  },
  stepTitle: { fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 10, fontFamily: "'Syne', sans-serif" },
  stepSub: { fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: 32 },

  featureRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 36 },
  featureCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 14, padding: '20px 14px', textAlign: 'center',
  },
  featureIcon: {
    width: 44, height: 44, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
  },
  featureLabel: { fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 4 },
  featureDesc: { fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 },

  apiBox: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '18px 18px 14px', marginBottom: 16, textAlign: 'left' },
  apiBoxLabel: { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 },
  apiKeyRow: { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: '10px 12px' },
  apiCode: { flex: 1, fontFamily: 'monospace', fontSize: 13, color: '#fcd34d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  infoBox: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: 'rgba(108,99,255,0.08)', border: '1px solid rgba(108,99,255,0.2)',
    borderRadius: 10, padding: '12px 14px',
    fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 28, textAlign: 'left',
  },
  inlineCode: { background: 'rgba(255,255,255,0.1)', color: '#c4b5fd', padding: '1px 5px', borderRadius: 4, fontSize: 13, fontFamily: 'monospace' },

  codeBox: { background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden', marginBottom: 20, textAlign: 'left' },
  codeBoxBar: { background: '#161b22', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' },
  code: { margin: 0, padding: '18px 18px', fontFamily: "'Fira Code','Courier New',monospace", fontSize: 12, color: '#e2e8f0', lineHeight: 2, overflowX: 'auto', whiteSpace: 'pre' },

  platformRow: { marginBottom: 28 },
  platformTag: { padding: '5px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, fontSize: 12, color: 'rgba(255,255,255,0.5)' },

  copyBtn: { padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', transition: 'all 0.2s', whiteSpace: 'nowrap' },

  btnRow: { display: 'flex', gap: 12, justifyContent: 'center' },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '13px 28px', background: 'linear-gradient(135deg,#6c63ff,#c084fc)',
    color: '#fff', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
  },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '13px 22px', background: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },

  skipBtn: {
    marginTop: 20, background: 'transparent', color: 'rgba(255,255,255,0.3)',
    border: 'none', fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
    display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1,
  },
};

export default Onboarding;